import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Globe, Award, BookOpen, Users, Star, Calendar, Menu, X } from 'lucide-react';

const Experts = () => {
  const [language, setLanguage] = useState<'pl' | 'en'>('pl');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const content = {
    pl: {
      title: 'Poznaj Naszych Ekspertów',
      subtitle: '"Education through relationship building"',
      experts: [
        {
          name: 'dr David O\'Keeffe',
          subject: 'Biology SL & HL',
          dates: 'SL: 2-4.02, HL: 4-6.02.2026',
          experience: '18+ lat doświadczenia w szkołach międzynarodowych',
          specialization: 'Biologia i chemia IB DP',
          description: 'To wybitny pedagog z ponad 18-letnim doświadczeniem w nauczaniu Biologii i Chemii w prestiżowym programie IB DP. Swoją drogę zawodową budował w renomowanych szkołach międzynarodowych oraz w Wielkiej Brytanii, przygotowując studentów medycyny i kandydatów na Oxford oraz Cambridge. Jako pasjonat nauki głęboko wierzy, że każdy uczeń może osiągnąć spektakularny progres, jeśli praca opiera się na partnerskiej relacji i wzajemnym zaufaniu. W swoim podejściu kładzie ogromny nacisk na ideę międzynarodowości oraz misję programu CAS, ucząc młodzież, jak wykorzystywać swój potencjał dla dobra społeczeństwa. Nie jest teoretykiem zamkniętym w gabinecie, lecz aktywnym mentorem, który towarzyszy uczniom w każdym kroku ich edukacyjnej drogi. Jego zajęcia charakteryzują się rzetelnością merytoryczną połączoną z poczuciem humoru i pasją. Z sukcesami pomaga młodym ludziom nie tylko zdać trudne egzaminy, ale przede wszystkim pokochć naukę i zrozumieć procesy rządzące światem. To ekspert, który łączy brytyjską precyzję nauczania z nowoczesnym, humanistycznym podejściem do rozwoju młodego człowieka. Dzięki niemu biologia i chemia przestają być wyzwaniem, a stają się pasją otwierającą drzwi do najlepszych uczelni medycznych i badawczych.',
          motto: 'Niech siła napędowa protonów będzie z Tobą!',
          image: './images/David.jpeg',
          achievements: [
            'Przygotowanie setek studentów medycyny',
            'Specjalista Oxbridge',
            'Ekspert IB DP Biology & Chemistry'
          ]
        },
        {
          name: 'dr Bill Roberts',
          subject: 'Mathematics AA SL/HL & TOK',
          dates: 'SL: 9-11.02, HL: 11-13.02.2026',
          experience: '30+ lat doświadczenia',
          specialization: 'Matematyka IB i TOK',
          description: 'Bill Roberts to wybitny specjalista, który swoje pierwsze szlify akademickie i uprawnienia pedagogiczne zdobywał na prestiżowym Uniwersytecie w Oksfordzie. Z edukacją związany jest od lat 80., a nauczanie matematyki stanowi fundament i największą pasję jego kariery. Posiada ponad 30-letnie doświadczenie w programie IB, prowadząc kursy Analysis and Approaches (AA) na obu poziomach zaawansowania (HL i SL). Jego autorytet potwierdza funkcja starszego egzaminatora i lidera zespołu IB, którą pełni nieprzerwanie od 1996 roku. Bill wierzy, że kluczem do sukcesu jest budowanie pewności siebie ucznia poprzez rozwijanie umiejętności logicznego myślenia i dostrzegania matematycznych wzorców. Jako doradca programowy (Curriculum Advisor) w Pamoja Education, od dekady współtworzy standardy nauczania matematyki oraz Teorii Wiedzy (TOK). Jego globalne doświadczenie obejmuje pracę w szkołach w Egipcie i Tanzanii, co pozwala mu świetnie rozumieć potrzeby uczniów z różnych kultur. Obecnie mieszka w północno-wschodniej Anglii, skąd z sukcesami wspiera młodzież w osiąganiu akademickich szczytów. To mentor, który łączy rzetelność oksfordzkiego wykształcenia z ciepłym i skutecznym podejściem do każdego podopiecznego.',
          motto: 'Budowanie pewności siebie poprzez logiczne myślenie',
          image: './images/Bill.jpg',
          achievements: [
            'Absolwent Uniwersytetu Oksfordzkiego',
            'Starszy Egzaminator IB od 1996',
            'Doradca Pamoja Education'
          ]
        },
        {
          name: 'mgr Anna Krzemińska-Kaczyńska',
          subject: 'Geography SL/HL',
          dates: 'SL: 2.02 (od 13:30) – 4.02.2026, HL: 5.02 – 6.02.2026',
          experience: '16+ lat doświadczenia w szkołach z maturą międzynarodową IB',
          specialization: 'Geografia IBDP',
          description: '21+ lat w edukacji (w tym 16+ lat w systemie IB); ekspertka przekłada złożone zagadnienia IB na przystępną i praktyczną wiedzę. Jej autorski sposób prowadzenia kursu Geography IBDP to fascynująca podróż badawcza, wzbogacona unikalnymi case studies z pracy na statkach Princess Cruises oraz światowych konferencji naukowych i młodzieżowych. Łącząc rzetelność akademicką z autentycznym wsparciem, buduje relacje, które motywują do osiągania najwyższych wyników. Dzięki temu kurs Geography IBDP nie tylko gwarantuje sukces na egzaminie, ale otwiera drzwi do prestiżowych uczelni i satysfakcjonującej kariery w duchu life-worthy balance.',
          motto: 'Mentoring z etycznym podejściem do edukacji',
          image: './images/IMG_7249.jpg',
          achievements: [
            'Global Teacher Award 2022',
            'Medal Komisji Edukacji Narodowej',
            'Absolwentka programu TEA USA'
          ]
        },
        {
          name: 'mgr Anna Przybyło',
          subject: 'English B / CAS',
          dates: 'English B, CAS: * Luty 2026: 2.02, 4.02, 6.02 oraz 9.02 Godziny: 17:00 – 19:00',
          experience: '20+ lat doświadczenia w sercu edukacji międzynarodowej',
          specialization: 'English B, Literatura, CAS',
          description: 'Doświadczona nauczycielka English B oraz Literature, koordynatorka CAS i metodyk. Z pasją wspiera sukcesy uczniów na maturze i w konkursach przedmiotowych, łącząc rzetelność naukową z kreatywnym podejściem. Jako ekspertka w koordynacji programów IB (PYP, MYP, DP - CAS) oraz mentor metodyczny, od lat z sukcesami szkoli kadrę nauczycielską w Polsce i za granicą. Jej misją jest wdrażanie światowych standardów nauczania poprzez budowanie głębokich relacji i autentyczne przywództwo edukacyjne.',
          motto: 'Kreatywność w nauce języka',
          image: './images/Anna Przybyło.jpg',
          achievements: [
            'Koordynatorka CAS',
            'Specjalista English B & Literature',
            'Metodyk języka angielskiego'
          ]
        },
        {
          name: 'dr Monika Lis',
          subject: 'Polish A: Literature',
          dates: 'Polish Literature (Luty 2026): 2–6 lutego, w godzinach 17:00–19:00',
          experience: '20+ lat doświadczenia: od uczelni wyższych po licea',
          specialization: 'Literatura polska IB',
          description: 'Doktor nauk humanistycznych i specjalistka w zakresie literatury polskiej. Jako ekspertka w przygotowaniu uczniów do wymagających egzaminów IBDP Literature A, kładzie szczególny nacisk na głęboką, wielopoziomową analizę tekstów. Jest doświadczonym nauczycielem akademickim z wieloletnią praktyką w nauczaniu języka polskiego jako obcego oraz posiada ogromne doświadczenie w koordynowaniu kursów Literature A w formule self-taught. Obecnie pełni również funkcję adiunkta na jednej z krakowskich uczelni.',
          motto: 'Precyzja w analizie literackiej',
          image: './images/Monika.jpeg',
          achievements: [
            'Doktor nauk humanistycznych',
            'Specjalista literatury polskiej',
            'Ekspert IB Polish A'
          ]
        }
      ]
    },
    en: {
      title: 'Meet Our Experts',
      subtitle: '"Education through relationship building"',
      experts: [
        {
          name: 'Dr David O\'Keeffe',
          subject: 'Biology SL/HL',
          dates: 'SL: 2-4.02, HL: 4-6.02.2026',
          experience: '18+ years of experience in international schools',
          specialization: 'IB DP Biology and Chemistry',
          description: 'He is an outstanding educator with over 18 years of experience teaching Biology and Chemistry in the prestigious IB DP program. He built his professional career in renowned international schools and in Great Britain, preparing medical students and candidates for Oxford and Cambridge. As a science enthusiast, he deeply believes that every student can achieve spectacular progress if the work is based on partnership and mutual trust. In his approach, he places great emphasis on the idea of internationalism and the mission of the CAS program, teaching young people how to use their potential for the good of society. He is not a theorist locked in an office, but an active mentor who accompanies students in every step of their educational journey. His classes are characterized by substantive reliability combined with a sense of humor and passion. He successfully helps young people not only pass difficult exams, but above all to fall in love with science and understand the processes governing the world. He is an expert who combines British teaching precision with a modern, humanistic approach to young person development. Thanks to him, biology and chemistry cease to be a challenge and become a passion that opens doors to the best medical and research universities.',
          motto: 'May the driving force of protons be with you!',
          image: './images/David.jpeg',
          achievements: [
            'Prepared hundreds of medical students',
            'Oxbridge specialist',
            'IB DP Biology & Chemistry expert'
          ]
        },
        {
          name: 'Dr Bill Roberts',
          subject: 'Mathematics AA SL/HL & TOK',
          dates: 'SL: 9-11.02, HL: 11-13.02.2026',
          experience: '30+ years of experience',
          specialization: 'IB Mathematics and TOK',
          description: 'Bill Roberts is an outstanding specialist who gained his first academic credentials and teaching qualifications at the prestigious University of Oxford. He has been involved in education since the 1980s, with mathematics teaching being the foundation and greatest passion of his career. He has over 30 years of experience in the IB programme, teaching Analysis and Approaches (AA) courses at both levels of advancement (HL and SL). His authority is confirmed by his role as senior examiner and IB team leader, which he has held continuously since 1996. Bill believes that the key to success is building student confidence through developing logical thinking skills and recognizing mathematical patterns. As a Curriculum Advisor at Pamoja Education, he has been co-creating mathematics and Theory of Knowledge (TOK) teaching standards for a decade. His global experience includes working in schools in Egypt and Tanzania, which allows him to understand the needs of students from different cultures perfectly. He currently lives in north-eastern England, from where he successfully supports young people in achieving academic heights. He is a mentor who combines the reliability of Oxford education with a warm and effective approach to each student.',
          motto: 'Building confidence through logical thinking',
          image: './images/Bill.jpg',
          achievements: [
            'Oxford University graduate',
            'IB Senior Examiner since 1996',
            'Pamoja Education advisor'
          ]
        },
        {
          name: 'Anna Krzemińska-Kaczyńska, MA',
          subject: 'Geography SL/HL',
          dates: 'SL: 2.02 (from 13:30) – 4.02.2026, HL: 5.02 – 6.02.2026',
          experience: '16+ years of experience in schools with IB Diploma Programme',
          specialization: 'Geography IBDP',
          description: '21+ years in education (including 16+ years in the IB system); expert translates complex IB concepts into accessible and practical knowledge. Her unique approach to teaching Geography IBDP is a fascinating research journey, enriched with unique case studies from work on Princess Cruises ships and global academic and youth conferences. Combining academic rigor with authentic support, she builds relationships that motivate students to achieve the highest results. Thanks to this, the Geography IBDP course not only guarantees exam success, but opens doors to prestigious universities and a satisfying career in the spirit of life-worthy balance.',
          motto: 'Mentoring with ethical approach to education',
          image: './images/IMG_7249.jpg',
          achievements: [
            'Global Teacher Award 2022',
            'Medal of National Education Commission',
            'US TEA programme graduate'
          ]
        },
        {
          name: 'Anna Przybyło, MA',
          subject: 'English B / CAS',
          dates: 'English B, CAS: * February 2026: 2.02, 4.02, 6.02 and 9.02 Hours: 17:00 – 19:00',
          experience: '20+ years of experience at the heart of international education',
          specialization: 'English B, Literature, CAS',
          description: 'Experienced English B and Literature teacher, CAS coordinator and methodologist. Passionately supports student success in exams and subject competitions, combining scientific rigor with creative approach. As an expert in coordinating IB programmes (PYP, MYP, DP - CAS) and methodological mentor, she has been successfully training teaching staff in Poland and abroad for years. Her mission is to implement global teaching standards through building deep relationships and authentic educational leadership.',
          motto: 'Creativity in language learning',
          image: './images/Anna Przybyło.jpg',
          achievements: [
            'CAS Coordinator',
            'English B & Literature specialist',
            'English language methodologist'
          ]
        },
        {
          name: 'Dr Monika Lis',
          subject: 'Polish A: Literature',
          dates: 'Polish Literature (February 2026): 2–6 February, 17:00–19:00',
          experience: '20+ years of experience: from universities to high schools',
          specialization: 'Polish Literature IB',
          description: 'Doctor of Humanities and specialist in Polish literature. As an expert in preparing students for demanding IBDP Literature A exams, she places particular emphasis on deep, multi-level text analysis. She is an experienced academic teacher with many years of practice in teaching Polish as a foreign language and has extensive experience in coordinating Literature A courses in self-taught format. She currently also serves as an assistant professor at one of Krakow\'s universities.',
          motto: 'Precision in literary analysis',
          image: './images/Monika.jpeg',
          achievements: [
            'Doctor of Humanities',
            'Polish literature specialist',
            'IB Polish A expert'
          ]
        }
      ]
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-yellow-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">IB</span>
            </div>
            <span className="font-bold text-xl">DP Accelerator</span>
          </Link>
          
          <div className="hidden md:flex space-x-6">
            <Link to="/" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Strona główna' : 'Home'}</Link>
            <Link to="/experts" className="text-blue-600 font-semibold">{language === 'pl' ? 'Eksperci' : 'Experts'}</Link>
            <Link to="/schedule" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Harmonogram' : 'Schedule'}</Link>
            <Link to="/course-topics" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Tematy' : 'Topics'}</Link>
            <Link to="/pricing" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Dołącz do kursu' : 'Join the DP Accelerator'}</Link>
            <Link to="/code-of-conduct" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Regulamin' : 'Code of Conduct'}</Link>
            <Link to="/contact" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Kontakt' : 'Contact'}</Link>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}
              className="flex items-center space-x-1"
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'pl' ? 'EN' : 'PL'}</span>
            </Button>
            
            {/* Mobile menu button */}
            <Button
              variant="outline"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b shadow-lg">
            <div className="container mx-auto px-4 py-4 space-y-4">
              <Link 
                to="/" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Strona główna' : 'Home'}
              </Link>
              <Link 
                to="/experts" 
                className="block text-blue-600 font-semibold"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Eksperci' : 'Experts'}
              </Link>
              <Link 
                to="/schedule" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Harmonogram' : 'Schedule'}
              </Link>
              <Link 
                to="/course-topics" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Tematy' : 'Topics'}
              </Link>
              <Link 
                to="/pricing" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Dołącz do kursu' : 'Join the DP Accelerator'}
              </Link>
              <Link 
                to="/code-of-conduct" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Regulamin' : 'Code of Conduct'}
              </Link>
              <Link 
                to="/contact" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Kontakt' : 'Contact'}
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Header */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-yellow-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-yellow-200 bg-clip-text text-transparent">
            {t.title}
          </h1>
          <p className="text-xl md:text-2xl text-blue-100">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Experts Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8">
            {t.experts.map((expert, index) => {
              // Professional avatar fallbacks for experts without custom photos
              const avatarImages = [
                'https://api.dicebear.com/7.x/avataaars/svg?seed=DavidOKeeffe&backgroundColor=b6e3f4&clothesColor=262e33&eyebrowType=default&eyeType=default&facialHairColor=auburn&facialHairType=default&hairColor=auburn&hatColor=ff488e&mouthType=smile&skinColor=light&topType=shortHairShortFlat', // David O'Keeffe
                'https://api.dicebear.com/7.x/avataaars/svg?seed=BillRoberts&backgroundColor=c0aede&clothesColor=3c4f5c&eyebrowType=default&eyeType=default&facialHairColor=brown&facialHairType=default&hairColor=brown&hatColor=ff488e&mouthType=smile&skinColor=light&topType=shortHairShortWaved', // Bill Roberts
                'https://api.dicebear.com/7.x/avataaars/svg?seed=AnnaKrzeminska&backgroundColor=ffd93d&clothesColor=ff488e&eyebrowType=default&eyeType=default&facialHairType=blank&hairColor=brown&mouthType=smile&skinColor=light&topType=longHairStraight', // Anna Krzemińska-Kaczyńska
                'https://api.dicebear.com/7.x/avataaars/svg?seed=AnnaPrzybylo&backgroundColor=b6e3f4&clothesColor=65c9ff&eyebrowType=default&eyeType=default&facialHairType=blank&hairColor=blonde&mouthType=smile&skinColor=light&topType=longHairBigHair', // Anna Przybyło
                'https://api.dicebear.com/7.x/avataaars/svg?seed=MonikaLis&backgroundColor=b6e3f4&clothesColor=262e33&eyebrowType=default&eyeType=default&facialHairType=blank&hairColor=black&mouthType=smile&skinColor=light&topType=longHairStraight2'  // Monika Lis
              ];
              
              return (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <CardTitle className="text-2xl mb-2">{expert.name}</CardTitle>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Badge variant="default" className="bg-blue-600">
                          {expert.subject}
                        </Badge>
                        <Badge variant="outline">
                          {expert.experience}
                        </Badge>
                      </div>
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <Calendar className="w-4 h-4 mr-1" />
                        <span>{expert.dates}</span>
                      </div>
                    </div>
                    <div className="w-20 h-20 rounded-full overflow-hidden ml-4 border-2 border-blue-200">
                      <img 
                        src={expert.image || avatarImages[index]}
                        alt={expert.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{language === 'pl' ? 'Specjalizacja' : 'Specialization'}</h4>
                      <p className="text-gray-600">{expert.specialization}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{language === 'pl' ? 'O ekspercie' : 'About the expert'}</h4>
                      <p className="text-gray-600 leading-relaxed">{expert.description}</p>
                    </div>
                    
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">{language === 'pl' ? 'Motto' : 'Motto'}</h4>
                      <p className="text-blue-700 italic">"{expert.motto}"</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{language === 'pl' ? 'Osiągnięcia' : 'Achievements'}</h4>
                      <ul className="space-y-1">
                        {expert.achievements.map((achievement, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <Star className="w-4 h-4 text-yellow-500 mr-2 flex-shrink-0" />
                            <span>{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="mt-4 text-xs text-gray-400 font-medium text-center">
                    #EducationExcellence #QualityTourism #TimeLeverage #ValuesDrivenEducation #TrustInTeaching #SustainablePerformance
                  </div>
                </CardContent>
              </Card>
            );
            })}
          </div>
        </div>
      </section>

      {/* Individual Sessions Info */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6 text-gray-900">
              {language === 'pl' ? 'Sesje Indywidualne' : 'Individual Sessions'}
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              {language === 'pl' 
                ? 'Oprócz zajęć grupowych, oferujemy możliwość rezerwacji indywidualnych sesji konsultacyjnych z naszymi ekspertami.'
                : 'In addition to group classes, we offer the possibility to book individual consultation sessions with our experts.'}
            </p>
            
            <Card className="text-left">
              <CardHeader>
                <CardTitle className="text-2xl">
                  {language === 'pl' ? 'Konsultacje Q&A' : 'Q&A Consultations'}
                </CardTitle>
                <CardDescription>
                  {language === 'pl' ? 'Codziennie 17:00 - 19:00' : 'Daily 17:00 - 19:00'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-3">
                      {language === 'pl' ? 'Dostępne konsultacje:' : 'Available consultations:'}
                    </h4>
                    <ul className="space-y-2">
                      <li className="flex items-center">
                        <BookOpen className="w-4 h-4 text-blue-600 mr-2" />
                        <span>Extended Essay (EE)</span>
                      </li>
                      <li className="flex items-center">
                        <BookOpen className="w-4 h-4 text-blue-600 mr-2" />
                        <span>Theory of Knowledge (TOK)</span>
                      </li>
                      <li className="flex items-center">
                        <BookOpen className="w-4 h-4 text-blue-600 mr-2" />
                        <span>Internal Assessments (IAs)</span>
                      </li>
                      <li className="flex items-center">
                        <BookOpen className="w-4 h-4 text-blue-600 mr-2" />
                        <span>CAS {language === 'pl' ? 'refleksje' : 'reflections'}</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">
                      {language === 'pl' ? 'Szczegóły:' : 'Details:'}
                    </h4>
                    <ul className="space-y-2 text-gray-600">
                      <li>• {language === 'pl' ? '45 minut sesji 1:1' : '45-minute 1:1 sessions'}</li>
                      <li>• {language === 'pl' ? 'Koszt: 100 GBP / 490 PLN' : 'Cost: 100 GBP / 490 PLN'}</li>
                      <li>• {language === 'pl' ? 'Ograniczona liczba miejsc' : 'Limited availability'}</li>
                      <li>• {language === 'pl' ? 'Rezerwacja wymagana' : 'Booking required'}</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-yellow-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">IB</span>
              </div>
              <span className="font-bold text-xl">DP Accelerator</span>
            </div>
            <p className="text-gray-400">
              {language === 'pl' 
                ? 'Intensywne kursy przygotowawcze do matury międzynarodowej IB.' 
                : 'Intensive preparation courses for the International Baccalaureate.'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Experts;